import cn from 'classnames';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Input,
  InputSearch,
  RadioButtonsGroup,
  Selector,
  Tag,
  TaptapList,
  Toast
} from 'sonata-react-components';
import { formatZoneArea } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getRetargetingTags } from './helpers/getSectionHeaderTags.helper';

import type { DropdownOption, SelectorOptionPropsInterface } from 'sonata-react-components';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';

import './retargeting.styles.scss';

const ID = 'RETARGETING';
const SELECTOR_OPTIONS = ['targeting', 'retargeting', 'both', 'exclude'];
const FILTER_OPTIONS = ['allActions', ...SELECTOR_OPTIONS];

const OPTIONS_DICTIONARY = {
  both: 'hybrid',
  exclude: 'exclude',
  hybrid: 'both',
  none: 'targeting',
  only: 'retargeting',
  retargeting: 'only',
  targeting: 'none'
};

const WHEN_DICTIONARY = {
  withInTheLastDay: 1,
  withInTheLastMonth: 30,
  withInTheLastTwoMonths: 60,
  withInTheLastWeek: 7
};

const HOW_DICTIONARY = {
  custom: null,
  justOnce: 1,
  moreThanOnce: 2
};

type TaptapListItem = {
  id: string;
  country: string;
  dimension: string;
  title: string;
  retargetingType?: string;
};

type Props = {
  readonly className?: string;
  readonly zones?: Zone[];
  onSetZones: (zones: Zone[]) => void;
};

type State = {
  zonesSelected: string[];
  optionFilterSelected: 'allActions' | 'retargeting' | 'targeting' | 'both' | 'exclude';
  optionChangeSelected: 'retargeting' | 'targeting' | 'both' | 'exclude';
  searchTerm: string;
};

const DEFAULT_STATE: State = {
  optionChangeSelected: 'retargeting',
  optionFilterSelected: 'allActions',
  searchTerm: '',
  zonesSelected: [] as string[]
};

export function Retargeting(props: Props): JSX.Element {
  const { className, zones, onSetZones } = props;
  const { formatMessage } = useIntl();
  const {
    setProp,
    targeting: { retargeting },
    resetSection
  } = useTargetingContext();
  const [showCustomInput, setShowCustomInput] = useState(retargeting.enabled && retargeting.minTimesInZone > 2);
  const [{ zonesSelected, optionFilterSelected, optionChangeSelected, searchTerm }, setRetargetingState] =
    useState<State>({ ...DEFAULT_STATE });

  const zonesMemoized = useMemo(
    () =>
      Array.isArray(zones)
        ? zones.map(zone => {
            const dimension = formatZoneArea(zone.area!, 'metric');
            return {
              country:
                worldCountries.find(country => country.cca3 === zone.country.toUpperCase())?.name.common || 'N/A',
              dimension: `${dimension.value} ${dimension.type}`,
              id: zone._id,
              location: zone.name,
              retargetingType: zone?.retargetingType || 'none'
            };
          })
        : [],
    [zones]
  );

  const zonesNormalized = zonesMemoized.filter(zone => {
    const matchesSearch = zone.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      optionFilterSelected === 'allActions' ? true : zone.retargetingType === OPTIONS_DICTIONARY[optionFilterSelected];
    return matchesSearch && matchesFilter;
  });

  const setRetargeting = (newRetargeting: typeof retargeting, enabled = true) => {
    newRetargeting.enabled = enabled;
    setProp('retargeting', newRetargeting);
  };

  const handleStateChange = <K extends keyof State>(key: K, value: State[K]) => {
    setRetargetingState(prevState => ({
      ...prevState,
      [key]: value
    }));
  };

  const handleOptionFilterChange = (option: SelectorOptionPropsInterface) => {
    handleStateChange('optionFilterSelected', option.id as State['optionFilterSelected']);
  };

  const handleOptionChangeChange = (option: SelectorOptionPropsInterface) => {
    handleStateChange('optionChangeSelected', option.id as State['optionChangeSelected']);
  };

  const handleSearchTermChange = (term: string) => {
    handleStateChange('searchTerm', term);
  };

  const handleActionChange = (zoneId: string, newValue: keyof typeof OPTIONS_DICTIONARY) => {
    if (Array.isArray(zones)) {
      const newZones = zones.map(z => {
        if (zoneId === z._id) {
          if (newValue === 'targeting') {
            return {
              ...z,
              isModified: true,
              retargetingCandidate: false,
              retargetingType: OPTIONS_DICTIONARY[newValue]
            };
          }
          return {
            ...z,
            isModified: true,
            retargetingCandidate: true,
            retargetingType: OPTIONS_DICTIONARY[newValue]
          };
        }

        return z;
      });

      onSetZones(newZones);
      const newRetargeting = structuredClone(retargeting);
      newRetargeting.zoneIds = newZones.filter(z => z.retargetingCandidate).map(z => z._id!);
      setRetargeting(newRetargeting);
    }
  };

  const handleSelectionChange = (items: { id: string }[]) => {
    handleStateChange(
      'zonesSelected',
      items.map(i => i.id)
    );
  };

  const handleChangeToApply = () => {
    if (Array.isArray(zones)) {
      const newZones = zones.map(z => {
        if (zonesSelected.includes(z._id!)) {
          if (optionChangeSelected === 'targeting') {
            return {
              ...z,
              isModified: true,
              retargetingCandidate: false,
              retargetingType: OPTIONS_DICTIONARY[optionChangeSelected]
            };
          }
          return {
            ...z,
            isModified: true,
            retargetingCandidate: true,
            retargetingType: OPTIONS_DICTIONARY[optionChangeSelected]
          };
        }

        return z;
      });

      onSetZones(newZones);
      const newRetargeting = structuredClone(retargeting);
      newRetargeting.zoneIds = newZones.filter(z => z.retargetingCandidate).map(z => z._id!);
      setRetargeting(newRetargeting);
    }
  };

  const filterOptions: SelectorOptionPropsInterface[] = FILTER_OPTIONS.map(option => ({
    id: option,
    label: formatMessage({
      defaultMessage: option,
      id: `ioSettings.retargeting.options.${option}`
    })
  }));

  const changeOptions = SELECTOR_OPTIONS.map(option => ({
    hideDot: true,
    id: option,
    label: formatMessage({
      defaultMessage: option,
      id: `ioSettings.retargeting.options.${option}`
    })
  }));

  const compositionData: TaptapListItem[] = zonesNormalized.map(zone => {
    return {
      country: zone.country,
      dimension: zone.dimension,
      id: zone.id!,
      retargetingType: zone.retargetingType,
      title: zone.location
    };
  });

  const whenRadioButtonItems = (
    ['withInTheLastTwoMonths', 'withInTheLastMonth', 'withInTheLastWeek', 'withInTheLastDay'] as const
  ).map(item => ({
    id: item,
    text: formatMessage({
      defaultMessage: item,
      id: `ioSettings.retargeting.impactSettings.when.${item}`
    })
  }));

  const howRadioButtonItems = (['justOnce', 'moreThanOnce', 'custom'] as const).map(item => ({
    id: item,
    text: formatMessage({
      defaultMessage: item,
      id: `ioSettings.retargeting.impactSettings.how.${item}`
    })
  }));

  const handleWhenChange = (id: string) => {
    const newRetargeting = structuredClone(retargeting);
    const days = WHEN_DICTIONARY[id as keyof typeof WHEN_DICTIONARY];
    newRetargeting.sinceDays = days;
    setRetargeting(newRetargeting);
  };

  const handleHowChange = (id: string) => {
    if (id === 'custom') {
      setShowCustomInput(true);
    } else {
      const newRetargeting = structuredClone(retargeting);
      const frequency = HOW_DICTIONARY[id as Exclude<keyof typeof HOW_DICTIONARY, 'custom'>];
      newRetargeting.minTimesInZone = frequency;
      setRetargeting(newRetargeting);
      setShowCustomInput(false);
    }
  };

  const handleHowCustomChange = (frequency: number) => {
    const newRetargeting = structuredClone(retargeting);
    newRetargeting.minTimesInZone = Number(frequency);
    setRetargeting(newRetargeting);
  };

  const handleResetRetargeting = () => {
    setRetargetingState({ ...DEFAULT_STATE });
    resetSection('retargeting');
  };

  const allZonesRetargeting =
    Array.isArray(zones) && zones.length > 0 && zones.every(z => z.retargetingType === 'only');

  const isSectionOpened = retargeting.enabled;
  const retargetingTags = getRetargetingTags(retargeting);
  const currentOptionFilterSelected = filterOptions.filter(option => option.id === optionFilterSelected);
  const currentOptionChangeSelected = filterOptions.filter(option => option.id === optionChangeSelected);

  return (
    <div className={cn('retargeting', className)} id="retargeting">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        hasErrors={allZonesRetargeting}
        onReset={handleResetRetargeting}
        preffixId="retargeting"
        tags={retargetingTags}
        title={formatMessage({
          defaultMessage: 'Retargeting',
          id: 'ioSettings.retargeting.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Re-engage users who have previously interacted with your brand, campaigns, or physical locations.',
          id: 'ioSettings.retargeting.tooltip'
        })}
      >
        {allZonesRetargeting && (
          <Toast
            autoClose={false}
            className="settings__errorToast"
            icon="fal fa-exclamation-triangle"
            text={formatMessage({
              defaultMessage: 'At least one zone must be set to Targeting.',
              id: 'ioSettings.retargeting.allRetargetingError'
            })}
            type="danger"
          />
        )}
        <div className="retargeting__subtitle">
          {formatMessage({
            defaultMessage:
              'Retargeting allows you to show your ads only to people that have already visited any of the specified zones.',
            id: 'ioSettings.retargeting.subtitle'
          })}
        </div>
        <div className="retargeting__body">
          <section className="zoneManager">
            <header className="retargeting__topBar">
              <div className="retargeting__selectWrapper">
                <Selector
                  defaultValue={filterOptions.filter(option => option.id === 'allActions')}
                  onChange={handleOptionFilterChange}
                  options={filterOptions}
                  showSearch
                  type="simple"
                  value={currentOptionFilterSelected}
                />
              </div>
              <div className="retargeting__searchWrapper">
                <InputSearch
                  id={`retargeting_filter_searchInput`}
                  lens
                  name="retargeting_filter_searchInput"
                  onChange={handleSearchTermChange}
                  placeholder={formatMessage({
                    defaultMessage: 'Search by name',
                    id: 'ioSettings.retargeting.filters.searchPlaceholder'
                  })}
                  value={searchTerm}
                />
              </div>
            </header>
            <div className="retargeting__content">
              <TaptapList
                className="retargeting__list"
                data={compositionData}
                defaultSelectedItems={zonesSelected.map(z => ({ id: z }))}
                id={ID}
                onSelectionChange={(itemsSelected: { id: string }[]) => handleSelectionChange(itemsSelected)}
                selectionType="checkboxMiddle"
              >
                <TaptapList.ColumnsBar>
                  <TaptapList.ColumnsBar.SelectAll />
                  <TaptapList.ColumnsBar.Column
                    className="locationName"
                    description={formatMessage({
                      defaultMessage: 'Location',
                      id: 'ioSettings.retargeting.table.columns.location'
                    })}
                    id={'locationName'}
                  />
                  <TaptapList.ColumnsBar.Column
                    className="dimension"
                    description={formatMessage({
                      defaultMessage: 'Dimension',
                      id: 'ioSettings.retargeting.table.columns.dimension'
                    })}
                    id={'dimension'}
                  />
                  <TaptapList.ColumnsBar.Column
                    className="country"
                    description={formatMessage({
                      defaultMessage: 'Country',
                      id: 'ioSettings.retargeting.table.columns.country'
                    })}
                    id={'country'}
                  />
                </TaptapList.ColumnsBar>
                <TaptapList.RowsListVirtualized<TaptapListItem>
                  emptyStateDescription={formatMessage({
                    defaultMessage: 'Try adjusting your search or filters to find zones.',
                    id: 'ioSettings.retargeting.emptyState.description'
                  })}
                  emptyStateIcon="fal fa-empty-set"
                  emptyStateTitle={formatMessage({
                    defaultMessage: 'No results match your filters',
                    id: 'ioSettings.retargeting.emptyState.title'
                  })}
                >
                  {itemsList =>
                    itemsList.map(item => (
                      <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                        <span className="locationName">{item.title}</span>
                        <span className="dimension">{item.dimension}</span>
                        <span className="country">{item.country}</span>
                        <div className="actions">
                          <Tag
                            className="inventory__contentListTag"
                            dropdownConfig={{
                              closeOnClick: true,
                              onChange: (val?: DropdownOption) =>
                                handleActionChange(item.id, val!.id as keyof typeof OPTIONS_DICTIONARY),
                              options: changeOptions,
                              value: changeOptions.find(
                                val =>
                                  val.id === OPTIONS_DICTIONARY[item.retargetingType as keyof typeof OPTIONS_DICTIONARY]
                              )
                            }}
                            id={`appAndSiteMarketplaceTag${optionChangeSelected}-${item.id}`}
                          />
                        </div>
                      </TaptapList.RowsListVirtualized.Row>
                    ))
                  }
                </TaptapList.RowsListVirtualized>
              </TaptapList>
              <div className="retargeting__bulkActions">
                <label className="retargeting__bulkLabel" htmlFor="bulk-action-select">
                  {formatMessage({
                    defaultMessage: 'Changes to',
                    id: 'ioSettings.retargeting.bulkActions.changesTo'
                  })}
                </label>
                <div className="retargeting__bulkControlGroup">
                  <Selector
                    defaultValue={changeOptions.filter(option => option.id === 'targeting')}
                    onChange={handleOptionChangeChange}
                    options={changeOptions}
                    showSearch
                    type="simple"
                    value={currentOptionChangeSelected}
                  />
                  <ButtonUI
                    disabled={zonesSelected.length === 0}
                    onClick={handleChangeToApply}
                    shape="squareRounded"
                    text={formatMessage({
                      defaultMessage: 'Apply',
                      id: 'ioSettings.retargeting.change.applyButton'
                    })}
                    type="secondary"
                  />
                </div>
              </div>
            </div>
            <form className="retargeting__impactSettings">
              <fieldset className="retargeting__radioGroup">
                <legend className="retargeting__radioLegend">
                  {formatMessage({
                    defaultMessage: 'When do you want to make an impact?',
                    id: 'ioSettings.retargeting.impactSettings.when.legend'
                  })}
                </legend>
                <RadioButtonsGroup
                  className="retargeting__radioOptionsWrapper"
                  id={`retargeting_impactSettings_whenRadioButton`}
                  items={whenRadioButtonItems}
                  onChange={handleWhenChange}
                  value={whenRadioButtonItems.find(v => WHEN_DICTIONARY[v.id] === retargeting.sinceDays)!.id}
                  withContainer={false}
                />
              </fieldset>
              <fieldset className="retargeting__radioGroup">
                <legend className="retargeting__radioLegend">
                  {formatMessage({
                    defaultMessage: 'How often do you want to make an impact?',
                    id: 'ioSettings.retargeting.impactSettings.how.legend'
                  })}
                </legend>
                <RadioButtonsGroup
                  className="retargeting__radioOptionsWrapper"
                  id={`retargeting_impactSettings_howRadioButton`}
                  items={howRadioButtonItems}
                  onChange={handleHowChange}
                  value={
                    showCustomInput
                      ? howRadioButtonItems.find(v => v.id === 'custom')!.id
                      : howRadioButtonItems.find(v => HOW_DICTIONARY[v.id] === retargeting.minTimesInZone)!.id
                  }
                  withContainer={false}
                />
                {showCustomInput && (
                  <div className="retargeting__customInputWrapper">
                    <Input
                      id={`retargeting_impactSettings_customFrequency_input`}
                      name="retargeting_impactSettings_customFrequency_input-input"
                      notFloatingPlaceholder
                      onChange={inputValue => handleHowCustomChange(inputValue as unknown as number)}
                      placeholder={formatMessage({
                        defaultMessage: 'Enter custom frequency',
                        id: 'ioSettings.retargeting.impactSettings.how.customPlaceholder'
                      })}
                      shape="squareUI"
                      tiny
                      type="number"
                      value={retargeting.minTimesInZone}
                    />
                  </div>
                )}
              </fieldset>
            </form>
          </section>
        </div>
      </FormExpandableSection>
    </div>
  );
}
